/*
 * DEV-1276: Auto-apply the "In stock" availability filter when filtering by size.
 *
 * Shopify's storefront filtering matches a size filter (filter.v.option.<size>)
 * against variants regardless of stock, so products whose selected size is sold
 * out still show. Combining it with filter.v.availability=1 limits the results
 * to products where that size is in stock.
 *
 * Behaviour:
 * - When a size filter becomes active and no availability value is selected,
 *   the "In stock" (filter.v.availability=1) filter is applied automatically.
 * - When the last size filter is removed, the availability filter is removed
 *   again, but only if it was added automatically. If the customer selected it
 *   themselves (or arrived with it in the URL), it is left alone.
 * - If the customer deselects the availability filter while a size filter is
 *   active, that choice is respected until all size filters are cleared.
 * - Works for checkbox changes in the filter form (desktop sidebar and mobile
 *   drawer share the same form), the active-filter "remove"/"clear" links, and
 *   direct page loads with a size filter in the URL.
 *
 * Requires the "Availability" filter to be enabled in the Search & Discovery
 * app; without it Shopify ignores filter.v.availability, and this script does
 * nothing. The auto-added state is kept per collection path in sessionStorage.
 *
 * This file only hooks into the vendor collection-filters-form.js component
 * (which builds the URL from the form's FormData and from link hrefs); it does
 * not modify it.
 */
(function () {
  'use strict';

  // Lower-cased option names (and their filter param suffixes) that count as size.
  const SIZE_OPTION_NAMES = ['størrelse', 'storrelse', 'størrelser', 'size', 'sizes', 'str', 'str.', 'größe', 'groesse'];
  const SIZE_PARAM_PREFIX = 'filter.v.option.';
  const AVAILABILITY_PARAM = 'filter.v.availability';
  const IN_STOCK_VALUE = '1';
  const FORM_SELECTOR = '[data-collection-filters-form]';
  const URL_LINK_SELECTOR = '[data-filter-update-url]';
  const STATE_AUTO = 'auto'; // availability was added by this script
  const STATE_OPT_OUT = 'opt-out'; // customer removed it while a size filter was active
  const STORAGE_KEY = 'theme:size-availability:' + window.location.pathname;

  function getState() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function setState(value) {
    try {
      if (value) {
        window.sessionStorage.setItem(STORAGE_KEY, value);
      } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      // Storage unavailable: behave as if nothing was auto-added.
    }
  }

  function isSizeParam(name) {
    if (!name || name.indexOf(SIZE_PARAM_PREFIX) !== 0) return false;
    let optionName = name.slice(SIZE_PARAM_PREFIX.length);
    try {
      optionName = decodeURIComponent(optionName);
    } catch (e) {
      // Keep the raw name.
    }
    return SIZE_OPTION_NAMES.indexOf(optionName.toLowerCase()) !== -1;
  }

  function getAvailabilityInputs(form) {
    return Array.prototype.filter.call(form.querySelectorAll('input[name]'), (input) => input.name === AVAILABILITY_PARAM);
  }

  // The availability filter only works when it is enabled in Search & Discovery,
  // in which case Shopify renders it as a filter in the form.
  function isAvailabilityFilterEnabled() {
    return Array.prototype.some.call(document.querySelectorAll(FORM_SELECTOR), (form) => getAvailabilityInputs(form).length > 0);
  }

  // Form changes: runs in the capture phase, before the theme's debounced
  // submit reads the form, so the auto-applied filter is part of the request.
  function onFormInput(event) {
    const target = event.target;
    if (!target || !target.name || !target.closest) return;
    const form = target.closest(FORM_SELECTOR);
    if (!form) return;

    const availabilityInputs = getAvailabilityInputs(form);
    if (!availabilityInputs.length) return;

    const sizeActive = Array.prototype.some.call(form.querySelectorAll('input[name]:checked'), (input) => isSizeParam(input.name));

    // The customer changed the availability filter themselves: it is theirs now.
    if (target.name === AVAILABILITY_PARAM) {
      const anyChecked = availabilityInputs.some((input) => input.checked);
      setState(!anyChecked && sizeActive ? STATE_OPT_OUT : null);
      return;
    }

    const state = getState();
    const inStockInput = availabilityInputs.find((input) => input.value === IN_STOCK_VALUE);
    const anyAvailabilityChecked = availabilityInputs.some((input) => input.checked);

    if (sizeActive) {
      if (!anyAvailabilityChecked && state !== STATE_OPT_OUT && inStockInput) {
        // Disabled inputs are left out of FormData.
        inStockInput.disabled = false;
        inStockInput.checked = true;
        setState(STATE_AUTO);
      }
    } else if (state === STATE_AUTO) {
      if (inStockInput) inStockInput.checked = false;
      setState(null);
    } else if (state === STATE_OPT_OUT) {
      setState(null);
    }
  }

  // Returns the URL with the availability filter added/removed as needed, or
  // null when nothing has to change.
  function normalizeUrl(url, currentParams) {
    const params = url.searchParams;
    const sizeActive = Array.from(params.keys()).some(isSizeParam);
    const hasAvailability = params.has(AVAILABILITY_PARAM);
    const state = getState();

    if (sizeActive) {
      if (hasAvailability) return null;
      // Removing the availability filter (e.g. its active-filter chip) while a
      // size filter stays active is a deliberate choice by the customer.
      if (currentParams && currentParams.has(AVAILABILITY_PARAM)) {
        setState(STATE_OPT_OUT);
        return null;
      }
      if (state === STATE_OPT_OUT) return null;
      params.append(AVAILABILITY_PARAM, IN_STOCK_VALUE);
      setState(STATE_AUTO);
      return url;
    }

    if (state === STATE_AUTO && hasAvailability) {
      params.delete(AVAILABILITY_PARAM);
      setState(null);
      return url;
    }
    if (state) setState(null);
    return null;
  }

  // Active-filter remove/clear links: rewrite the href before the theme's click
  // handler reads it.
  function onLinkClick(event) {
    const link = event.target && event.target.closest ? event.target.closest(URL_LINK_SELECTOR) : null;
    if (!link || !link.getAttribute('href') || !isAvailabilityFilterEnabled()) return;

    const url = new URL(link.getAttribute('href'), window.location.origin);
    const currentParams = new URL(window.location.href).searchParams;
    const normalized = normalizeUrl(url, currentParams);
    if (normalized) {
      link.setAttribute('href', normalized.pathname + normalized.search + normalized.hash);
    }
  }

  // Direct page loads (shared links, bookmarks) with a size filter in the URL.
  function onLoad() {
    if (!isAvailabilityFilterEnabled()) return;
    const url = new URL(window.location.href);
    if (!Array.from(url.searchParams.keys()).some(isSizeParam)) {
      // No size filter: forget any stale state, but never remove a filter on load.
      if (getState() === STATE_OPT_OUT) setState(null);
      return;
    }
    const normalized = normalizeUrl(url, null);
    if (normalized) {
      window.location.replace(normalized.toString());
    }
  }

  document.addEventListener('input', onFormInput, true);
  document.addEventListener('click', onLinkClick, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onLoad);
  } else {
    onLoad();
  }
})();
